import { useMemo } from 'react';

import {
    useAccountingOnboardingProgress,
    type OnboardingProgress,
} from 'services/bookkeeping/queries/useAccountingOnboardingProgress';

// Shared across both skip paths so the event name and reason can't drift.
export const AUTO_STATEMENTS_SKIPPED_EVENT = 'Auto Statements Skipped';
export const AUTO_STATEMENTS_SKIP_REASON = 'no_eligible_institutions';

/**
 * A single business account confirmed during the bank-connection step.
 */
export interface ConnectStatementsAccount {
    id: string;
    name: string;
    type: string;
    mask: string;
    institutionId: string;
    institutionName: string;
}

/**
 * Business accounts grouped by their institution. Auto Statements support is determined at the
 * institution level (Quiltt has no per-account-type support distinction), so the UI badges and the
 * Enable action operate per institution.
 */
export interface ConnectStatementsInstitution {
    institutionId: string;
    institutionName: string;
    accounts: ConnectStatementsAccount[];
}

interface UseConnectStatementsDataResult {
    institutions: ConnectStatementsInstitution[];
    isLoading: boolean;
}

/** A Quiltt connection the step has already recorded, keyed by the institution it was credited to. */
interface PersistedQuilttConnection {
    institutionId: string;
    connectionId: string;
}

/**
 * The connections the step persisted on an earlier visit — what makes a returning member's bank read
 * as enabled rather than supported-again. The single definition both the statements intro and
 * ConnectStatements read, so the two cannot count "already enabled" differently.
 */
export function getPersistedQuilttConnections(
    onboardingProgress: OnboardingProgress | undefined
): PersistedQuilttConnection[] {
    return (onboardingProgress?.connect_automated_statements_data?.quiltt_connections ?? []).flatMap((connection) =>
        connection.institution_id
            ? [{ institutionId: connection.institution_id, connectionId: connection.connection_id }]
            : []
    );
}

/** Resolvability-cache key for an account's institution: Plaid id, falling back to name. */
export function getInstitutionKey(account: { institution_id?: string; institution_name: string }): string {
    return account.institution_id ?? account.institution_name;
}

type ConfirmedAccount = NonNullable<
    NonNullable<OnboardingProgress['bank_connection_data']>['confirmed_business_accounts']
>[number];

/**
 * One entry per account id. The connect-account step has saved the same account more than once, and
 * a re-save can arrive without its institution id, so the copy that has one wins.
 */
function dedupeAccounts(accounts: ConfirmedAccount[]): ConfirmedAccount[] {
    const byId = new Map<string, ConfirmedAccount>();
    accounts.forEach((account) => {
        const existing = byId.get(account.id);
        if (!existing || (!existing.institution_id && account.institution_id)) {
            byId.set(account.id, account);
        }
    });
    return Array.from(byId.values());
}

/**
 * Sources the member's confirmed business accounts from the accounting-onboarding progress (the same
 * onboarding-native source `UploadDocuments` uses) and groups them by institution. Does NOT call the
 * bank-statements availability endpoint — that is the post-connection / settings signal.
 */
export default function useConnectStatementsData(): UseConnectStatementsDataResult {
    const { data: onboardingProgress, isLoading } = useAccountingOnboardingProgress();

    const institutions = useMemo<ConnectStatementsInstitution[]>(() => {
        const confirmedAccounts = dedupeAccounts(
            onboardingProgress?.bank_connection_data?.confirmed_business_accounts ?? []
        );

        const byInstitution = new Map<string, ConnectStatementsInstitution>();

        confirmedAccounts.forEach((account) => {
            // Group by Plaid institution id when available; fall back to the institution name so
            // accounts without an id still render under a single card rather than scattering.
            const institutionId = getInstitutionKey(account);

            if (!byInstitution.has(institutionId)) {
                byInstitution.set(institutionId, {
                    institutionId,
                    institutionName: account.institution_name,
                    accounts: [],
                });
            }

            byInstitution.get(institutionId)?.accounts.push({
                id: account.id,
                name: account.name,
                type: account.type,
                mask: account.mask,
                institutionId,
                institutionName: account.institution_name,
            });
        });

        return Array.from(byInstitution.values());
    }, [onboardingProgress?.bank_connection_data?.confirmed_business_accounts]);

    return { institutions, isLoading };
}
